/**
 * Anonymous session id for pre-auth analytics events (RFC-0974 §8).
 *
 * A UUID kept in `sessionStorage` so it survives the full-page OAuth redirect
 * (Google/Facebook return via a top-level navigation — Appendix A item 4). It is
 * rotated ONLY after an authenticated event fires (the anon→user stitch is done)
 * or on expiry — never per navigation. Cross-session persistence is intentionally
 * NOT required (hands-on B2B onboarding).
 *
 * Reading/writing `sessionStorage` here is consent-safe: the analytics SDK only
 * ever calls this once analytics consent is granted (the emitter gates first).
 */

import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('dw:analytics-events:session-id')

const STORAGE_KEY = 'dw_evt_session'
// Rotate a still-anonymous session after this long so a shared/kept-open tab does
// not accumulate one ever-growing funnel identity.
const MAX_AGE_MS = 24 * 60 * 60 * 1000

interface StoredSession {
  id: string
  createdAt: number
}

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through to the manual generator */
  }
  // RFC-4122-ish fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// In-memory fallback for privacy-restricted / sandboxed browsers where
// sessionStorage is blocked or throws. Without it, every getSessionId() would
// mint a new UUID (write silently fails) and fragment every pre-auth event into
// its own session, breaking funnel stitching.
let memorySession: StoredSession | null = null

function readStored(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed: Partial<StoredSession> = JSON.parse(raw)
      if (typeof parsed.id === 'string' && typeof parsed.createdAt === 'number') {
        return { id: parsed.id, createdAt: parsed.createdAt }
      }
    }
  } catch {
    /* corrupt / unavailable storage — fall back to the in-memory copy */
  }
  return memorySession
}

function write(session: StoredSession): void {
  // Always keep the in-memory copy so the id survives even when storage is
  // unavailable (the memory copy is per-tab, which is the session scope anyway).
  memorySession = session
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    /* storage unavailable — the in-memory copy above is the fallback */
  }
}

/** Return the current anonymous session id, creating/refreshing it as needed. */
export function getSessionId(): string {
  const existing = readStored()
  const now = Date.now()
  if (existing && now - existing.createdAt < MAX_AGE_MS) {
    return existing.id
  }
  const created: StoredSession = { id: newId(), createdAt: now }
  write(created)
  log({ event: 'session-id-created', expired: Boolean(existing) })
  return created.id
}

/**
 * Rotate the session id. Call AFTER an authenticated event fires (the anon→user
 * stitch is complete) so the next anonymous session is distinct.
 */
export function rotateSessionId(): void {
  const created: StoredSession = { id: newId(), createdAt: Date.now() }
  write(created)
  log({ event: 'session-id-rotated' })
}

/**
 * Clear the stored session id WITHOUT minting a new one. Used on consent
 * withdrawal: a user who opted out must not receive a fresh persistent analytics
 * identifier. `getSessionId()` lazily creates a new id only on the next call
 * (which only happens after consent is granted again and an event is emitted).
 */
export function clearSessionId(): void {
  memorySession = null
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** Test helper — clear the stored session (storage + in-memory fallback). */
export function _clearSessionForTest(): void {
  clearSessionId()
}
